import { autoUpdate, flip, shift, useFloating } from "@floating-ui/react";
import classNames from "classnames";
import findIndex from "lodash/findIndex";
import { ActionIcon } from "PFComponents/action_icon";
import DropDown, { DropdownOption } from "PFComponents/dropdown/dropdown";
import { ISO_TIME_FORMAT, TIME_FORMAT_12H, utc } from "PFCore/helpers/date";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { EventKey } from "PFTypes/event_key";
import { RefObject, SyntheticEvent, useCallback, useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useClickAway } from "react-use";

import css from "./time_picker.module.scss";

const isoTimeToMoment = (time: string) => utc(time, ISO_TIME_FORMAT);

type TimePickerProps = {
  value?: string | null;
  onChange: (value: string) => void;
  onBlur?: (event: SyntheticEvent) => void;
  step?: number; // in minutes
  portalRef?: React.RefObject<HTMLElement | null>;
  min?: string | null;
  max?: string | null;
  className?: string;
  disabled?: boolean;
  name?: string;
  placeholder?: string;
  fixedWidth?: boolean;
};

export const TimePicker = ({
  value,
  onChange,
  step = 15,
  min,
  max,
  portalRef,
  className,
  disabled,
  name,
  placeholder,
  onBlur,
  fixedWidth = true
}: TimePickerProps) => {
  const { t } = useTranslation("core");
  const { timeFormatString, formatISOTime } = useDateFormatter();

  const valueFormatted = useMemo(
    () => (value ? isoTimeToMoment(value).format(timeFormatString) : ""),
    [value, timeFormatString]
  );

  const [inputValue, setInputValue] = useState(valueFormatted);
  const [showDropdown, setShowDropdown] = useState(false);

  const { refs, floatingStyles } = useFloating({
    placement: "bottom-start",
    middleware: [flip(), shift()],
    whileElementsMounted: autoUpdate
  });

  useEffect(() => setInputValue(valueFormatted), [valueFormatted]);

  useClickAway(refs.reference as RefObject<HTMLElement>, (e) => {
    if (portalRef?.current && portalRef.current.contains(e.target as Node)) {
      return;
    }
    setShowDropdown(false);
  });

  const width = fixedWidth ? (timeFormatString === TIME_FORMAT_12H ? 110 : 84) : undefined;

  const handleChange = (newValue) => {
    setShowDropdown(false);
    onChange(formatISOTime(utc(newValue, timeFormatString)));
  };

  const handleIconClick = () => setShowDropdown((prev) => !prev);

  const dropdownOptions = useMemo<DropdownOption[]>(() => {
    const startTime = (min ? isoTimeToMoment(min) : utc().startOf("day")).subtract(step, "m");
    const endTime = (max ? isoTimeToMoment(max) : utc().endOf("day")).subtract(step, "m");
    const options = [] as DropdownOption[];
    while (startTime < endTime) {
      const timeOption = startTime.add(step, "m").format(timeFormatString);
      options.push({
        displayElement: timeOption,
        item: timeOption,
        id: timeOption
      });
    }
    return options;
  }, [step, timeFormatString, min, max]);
  const selecteIndex = findIndex(dropdownOptions, ["item", inputValue]);

  // input handlers
  const handleValueOnInputChange = useCallback(() => {
    if (valueFormatted === inputValue) {
      return;
    }
    const momentInputValue = utc(inputValue, timeFormatString);
    const isBelowMax = max ? momentInputValue < isoTimeToMoment(max) : true;
    const isAboveMin = min ? momentInputValue > isoTimeToMoment(min) : true;
    if (momentInputValue.isValid() && isAboveMin && isBelowMax) {
      onChange(formatISOTime(momentInputValue));
    } else {
      setInputValue(valueFormatted);
    }
  }, [formatISOTime, max, min, inputValue, onChange, valueFormatted, timeFormatString]);

  const handleInputChange = (event) => setInputValue(event.target.value);
  const handleInputBlur = useCallback(
    (event) => {
      handleValueOnInputChange();
      onBlur?.(event);
    },
    [handleValueOnInputChange, onBlur]
  );

  const handleInputKeyDown = useCallback(
    (event) => {
      if (event.key === EventKey.Enter) {
        handleValueOnInputChange();
      } else if (event.key === EventKey.Escape) {
        setInputValue(valueFormatted); // reset to previous value
      }
    },
    [handleValueOnInputChange, valueFormatted]
  );

  const Dropdown = (
    <div ref={refs.setFloating} style={{ ...floatingStyles, zIndex: 20 }}>
      <DropDown
        style={{ minWidth: width }}
        dropDownClassName={css.dropdownItem}
        options={dropdownOptions}
        handleChange={handleChange}
        handleClose={() => setShowDropdown(false)}
        selectedIndex={selecteIndex}
      />
    </div>
  );

  return (
    <div className={classNames(css.container, className)} style={{ width }} ref={refs.setReference}>
      <input
        value={inputValue}
        name={name}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        aria-label={t("components.timePicker.ariaLabel")}
      />
      <ActionIcon name="created" size="sm" onClick={handleIconClick} disabled={disabled} />
      {showDropdown && (portalRef?.current ? ReactDOM.createPortal(Dropdown, portalRef.current) : Dropdown)}
    </div>
  );
};
